import { Link } from "react-router-dom";
import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * AboutCTA — closing invitation of the /about page, mirroring the home
 * Invitation and the ServicesCTA blocks so buttons/typography stay
 * identical across the site.
 */
export default function AboutCTA() {
  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const headlineRef = useEnterAnimation<HTMLHeadingElement>({ delay: 140 });
  const kickerRef = useEnterAnimation<HTMLParagraphElement>({ delay: 300 });
  const ctaRef = useEnterAnimation<HTMLDivElement>({ delay: 460 });
  const footerRef = useEnterAnimation<HTMLDivElement>({ delay: 640 });

  return (
    <section
      id="about-cta"
      className="relative w-full bg-background-100"
      style={{ minHeight: "100vh" }}
    >
      {/* Section meta */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            About Us / 06
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            When you&rsquo;re ready
          </span>
        </div>
      </div>

      {/* Decorative rings */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <div className="absolute w-[95vh] h-[95vh] rounded-full border border-foreground-950/[0.08]"></div>
        <div className="absolute w-[68vh] h-[68vh] rounded-full border border-foreground-950/[0.06]"></div>
      </div>

      <div className="relative z-10 w-full min-h-screen flex items-center justify-center px-6 md:px-10 py-28 md:py-36">
        <div className="max-w-[1100px] w-full mx-auto flex flex-col items-center text-center">
          <div ref={eyebrowRef} className="mb-10">
            <span className="inline-flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-foreground-950/70 font-medium">
              <span className="w-8 h-[1px] bg-foreground-950/35"></span>
              Come sit with us
              <span className="w-8 h-[1px] bg-foreground-950/35"></span>
            </span>
          </div>

          <h2
            ref={headlineRef}
            className="mb-10 max-w-[900px]"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(2.75rem, 6vw, 6.25rem)",
              fontWeight: 300,
              letterSpacing: "-0.03em",
              lineHeight: 1,
              color: "oklch(var(--foreground-950))",
              fontVariationSettings: "'opsz' 144",
            }}
          >
            The kettle is on.
            <br />
            <span className="italic text-foreground-950/85">
              Come when you can.
            </span>
          </h2>

          <p
            ref={kickerRef}
            className="italic mb-16 max-w-[640px]"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(1.1rem, 1.5vw, 1.5rem)",
              fontWeight: 300,
              lineHeight: 1.55,
              letterSpacing: "0.005em",
              color: "oklch(var(--foreground-950) / 0.72)",
              fontVariationSettings: "'opsz' 144",
            }}
          >
            No forms today. Just a first hello whenever you feel ready.
          </p>

          <div
            ref={ctaRef}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/contacts"
              className="group inline-flex items-center gap-3 bg-foreground-950 text-background-50 rounded-full pl-8 pr-2 py-2 whitespace-nowrap cursor-pointer transition-transform duration-300 hover:-translate-y-0.5"
            >
              <span className="text-[11px] tracking-[0.3em] uppercase font-medium">
                Book a Session
              </span>
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 group-hover:translate-x-0.5">
                <i className="ri-arrow-right-up-line text-lg"></i>
              </span>
            </Link>

            <Link
              to="/services"
              className="group inline-flex items-center gap-3 bg-foreground-950 text-background-50 rounded-full pl-8 pr-2 py-2 whitespace-nowrap cursor-pointer transition-transform duration-300 hover:-translate-y-0.5"
            >
              <span className="text-[11px] tracking-[0.3em] uppercase font-medium">
                Explore Services
              </span>
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 group-hover:translate-x-0.5">
                <i className="ri-arrow-right-up-line text-lg"></i>
              </span>
            </Link>
          </div>

          <div
            ref={footerRef}
            className="mt-20 flex items-center gap-3 text-foreground-950/55"
          >
            <Link
              to="/"
              className="text-[10px] tracking-[0.3em] uppercase font-medium cursor-pointer transition-colors duration-300 hover:text-foreground-950"
            >
              Return to home
            </Link>
            <span className="w-8 h-[1px] bg-foreground-950/40"></span>
            <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
              Serene
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}